import { FactoryIcon } from "@phosphor-icons/react/Factory";
import { PersonIcon } from "@phosphor-icons/react/Person";
import { TruckIcon } from "@phosphor-icons/react/Truck";
import type { ComponentType, Dispatch, SetStateAction } from "react";
import { ChoiceCards } from "@freightsimple/ui-core/Controls/ChoiceCards";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { AccountType } from "@/Shared/Authentication/Hooks/useAuthenticationCreateAccount";
import { AuthenticationAccountTypeCard } from "@/Shared/Authentication/CustomComponents/AuthenticationAccountTypeCard";

interface CreateAccountPageAccountTypeProps {
  accountType: AccountType | undefined;
  setAccountType: Dispatch<SetStateAction<AccountType | undefined>>;
}

// All three highlight in the brand colour — ChoiceCards' default, so the group
// says nothing about accent at all.
const accountTypeChoices: {
  value: AccountType;
  icon: ComponentType<{ className?: string }>;
  label: string;
}[] = [
  {
    value: AccountType.Personal,
    icon: PersonIcon,
    label: "Personal Shipment",
  },
  {
    value: AccountType.Business,
    icon: FactoryIcon,
    label: "Business Shipments",
  },
  {
    value: AccountType.Broker,
    icon: TruckIcon,
    label: "Logistics Company",
  },
];

// ChoiceCards hands back the raw string value; narrow it back to the union
// without an unsafe cast.
function toAccountType(value: string): AccountType | undefined {
  if (value === AccountType.Personal) return AccountType.Personal;
  if (value === AccountType.Business) return AccountType.Business;
  if (value === AccountType.Broker) return AccountType.Broker;
  return undefined;
}

export function AuthenticationCreateAccountPageAccountType(
  props: CreateAccountPageAccountTypeProps,
) {
  const { accountType, setAccountType } = props;
  return (
    <Stack gap={4}>
      <T weight="medium">
        First, are you shipping on behalf of an individual, a business, or a
        broker/logistics company?
      </T>
      <ChoiceCards
        value={accountType}
        onChange={(value) => setAccountType(toAccountType(value))}
        data-testid="signup-account-type"
        options={accountTypeChoices.map((choice) => ({
          value: choice.value,
          content: (
            <AuthenticationAccountTypeCard
              icon={choice.icon}
              label={choice.label}
            />
          ),
        }))}
      />
    </Stack>
  );
}
